import React, { useCallback } from 'react';
import { ILogt } from '../../models/logT';

interface BetaExportButtonsProps {
  data: ILogt[];
  disabled?: boolean;
}

const COLUMNS = [
  { key: 'logtTS',               label: 'Date/Time' },
  { key: 'Direction',            label: 'Direction' },
  { key: 'MessageType',          label: 'Type' },
  { key: 'SMI',                  label: 'SMI' },
  { key: 'Medium',               label: 'Medium' },
  { key: 'AircraftId',           label: 'Aircraft' },
  { key: 'FlightId',             label: 'Flight' },
  { key: 'Source',               label: 'Source' },
  { key: 'DepartureAirportIATA', label: 'Orig' },
  { key: 'ArrivalAirportIATA',   label: 'Dest' },
];

const getCellValue = (row: ILogt, key: string): string => {
  const r = row as any;
  if (key === 'logtTS') {
    const ts = r.logtTS || r.LogtTS;
    if (!ts) return '';
    const d = new Date(ts);
    const mm  = String(d.getMonth() + 1).padStart(2, '0');
    const dd  = String(d.getDate()).padStart(2, '0');
    const yy  = String(d.getFullYear()).slice(2);
    const hh  = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss  = String(d.getSeconds()).padStart(2, '0');
    return `${mm}/${dd}/${yy} ${hh}:${min}:${ss}`;
  }
  if (key === 'Direction') {
    const dir = r.Direction ?? r.direction;
    if (dir === 0 || dir === '0') return 'Uplink';
    if (dir === 1 || dir === '1') return 'Downlink';
    return String(dir ?? '');
  }
  const lo = key.charAt(0).toLowerCase() + key.slice(1);
  return String(r[key] ?? r[lo] ?? '');
};

const BetaExportButtons: React.FC<BetaExportButtonsProps> = ({ data, disabled }) => {
  const handlePrint = useCallback(() => {
    const rows = data.map(row =>
      `<tr>${COLUMNS.map(col => `<td>${getCellValue(row, col.key)}</td>`).join('')}</tr>`
    ).join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>ACARS History Beta — Print</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 11px; margin: 16px; }
            h2 { margin: 0 0 10px; font-size: 14px; }
            p.meta { margin: 0 0 8px; color: #555; font-size: 10px; }
            table { width: 100%; border-collapse: collapse; }
            th { background: #01426a; color: #fff; padding: 4px 6px; text-align: left; font-size: 10px; text-transform: uppercase; }
            td { padding: 3px 6px; border-bottom: 1px solid #ddd; font-size: 10px; }
            tr:nth-child(even) td { background: #f7f7f7; }
            @media print { @page { margin: 10mm; } }
          </style>
        </head>
        <body>
          <h2>ACARS History Beta</h2>
          <p class="meta">Printed: ${new Date().toLocaleString()} &mdash; ${data.length} records</p>
          <table>
            <thead><tr>${COLUMNS.map(c => `<th>${c.label}</th>`).join('')}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
          <script>window.onload = function(){ window.print(); window.close(); }<\/script>
        </body>
      </html>`;

    const win = window.open('', '_blank', 'width=1100,height=700');
    if (!win) return;
    win.document.write(html);
    win.document.close();
  }, [data]);

  const handleCSV = useCallback(() => {
    const header = COLUMNS.map(c => `"${c.label}"`).join(',');
    const rows = data.map(row =>
      COLUMNS.map(col => {
        const val = getCellValue(row, col.key).replace(/"/g, '""');
        return `"${val}"`;
      }).join(',')
    );
    const csvContent = [header, ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `acars-history-beta-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data]);

  return (
    <>
      <button
        type="button"
        className="beta-toolbar-export-btn"
        onClick={handlePrint}
        disabled={disabled}
        title="Print visible grid records"
      >
        🖨 Print
      </button>
      <button
        type="button"
        className="beta-toolbar-export-btn"
        onClick={handleCSV}
        disabled={disabled}
        title="Export visible grid records as CSV"
      >
        ⬇ CSV
      </button>
    </>
  );
};

export default BetaExportButtons;
